import React, { lazy, Suspense } from 'react';
import {
	Redirect,
	Route,
	Switch,
	useLocation,
	Link as RouterLink,
} from 'react-router-dom';

import Errors from 'components/structural/Errors';
import CartRouter from 'components/routers/CartRouter';
import ChatRouter from 'components/routers/ChatRouter';
import DomainsRouter from 'components/routers/DomainsRouter';
import HomeRouter from 'components/routers/HomeRouter';
import NetworkRouter from 'components/routers/NetworkRouter';
import ProjectsRouter from 'components/routers/ProjectsRouter';
import ServersRouter from 'components/routers/ServersRouter';
import ServicesRouter from 'components/routers/ServicesRouter';
import SecurityRouter from 'components/routers/SecurityRouter';
import ShopRouter from 'components/routers/ShopRouter';
import SupportRouter from 'components/routers/SupportRouter';
import FeedbackRouter from 'components/routers/FeedbackRouter';
import GlobalComponents from 'components/structural/GlobalComponents';
import DialogNewPortalWelcome from 'components/standard/DialogNewPortalWelcome';
import { DialogUTMWrapper } from 'components/standard/DialogUTM';
import { PagePreloadProvider } from 'modules/pagePreload';

import AccountRouter, {
	ACCOUNT_CREATE_PATH,
} from 'components/routers/AccountRouter';
import LeftHandLayout from 'containers/LeftHandLayout';
import useAccountDetails from 'modules/queries/account/useDetails';
import useOrderList from 'modules/queries/order/useList';
import RoleRestrictedRoute from 'utility/routing/RoleRestrictedRoute';
import { roles } from 'utility/constants/roles';
import accountStatuses from 'utility/constants/accountStatuses';
import useAuthenticated, {
	useKeroOnly,
	useBasketAdmin,
	useAccountLogin,
	useSuspended,
} from 'modules/oidc/useAuthenticated';
import OidcCallback from 'containers/pages/oidc/Callback';
import { useAuth } from 'react-oidc-context';
import SuspenseFallback from './SuspenseFallback';
import LoginRedirect from './LoginRedirect';

const Cart = lazy(() => import('containers/pages/shop/Cart'));
const Logout = lazy(() => import('containers/structural/Logout'));
const NotFound = lazy(() => import('pages/error/NotFound'));
const LiveChatLoading = lazy(
	() => import('containers/structural/LiveChatLoading'),
);
const LiveChatUnavailable = lazy(
	() => import('containers/structural/LiveChatUnavailable'),
);
const InvoicePDFDisplay = lazy(
	() => import('containers/pages/billing/invoice/InvoicePDFDisplay'),
);
const VNCConsole = lazy(
	() => import('containers/pages/servers/beyond-hosting/VNCConsole'),
);
const AccountCreate = lazy(() => import('containers/pages/account/create'));
const AccessDenied = lazy(() => import('pages/error/AccessDenied'));
const SupportCase = lazy(() => import('containers/pages/support/ticket'));
const OrderConfirmation = lazy(
	() => import('containers/pages/shop/OrderConfirmation/OrderConfirmation'),
);
const RestrictionNoAccountLogin = lazy(
	() => import('components/standard/Restrictions/RestrictionNoAccountLogin'),
);
const RestrictionAccountTerminated = lazy(
	() => import('components/standard/Restrictions/RestrictionAccountTerminated'),
);
const RestrictionVerificationRequired = lazy(
	() =>
		import('components/standard/Restrictions/RestrictionVerificationRequired'),
);
const InvoiceDetails = lazy(
	() => import('containers/pages/account/InvoiceDetails'),
);
const MigrationCenter = lazy(
	() => import('containers/pages/migration/MigrationCenter'),
);
const BackupsAndRecovery = lazy(
	() => import('containers/pages/storage/BackupsAndRecovery'),
);

const AlwaysRoutesRaw = () => [
	<Route key="shopRoute" path="/shop">
		<ShopRouter path="/shop" />
	</Route>,
	<Route key="cartBasketUuid" exact path="/cart/:basketUuid?">
		<Cart />
	</Route>,
	// legacy support for "saved baskets"
	<Route key="cartSavedBasketUuid" exact path="/cart/saved/:basketUuid">
		<Cart />
	</Route>,
];

const AuthenticatedRoutes = () => (
	<PagePreloadProvider fallback={<SuspenseFallback />}>
		<Switch>
			<Route path="/logout">
				<Logout />
			</Route>
			<Route exact path="/shop/complete" component={OrderConfirmation} />
			<Route path="/account/create">
				<AccountCreate />
			</Route>
			<Route path="/" exact>
				<HomeRouter />
			</Route>
			<Route path="/projects">
				<ProjectsRouter />
			</Route>
			<Route path="/servers">
				<ServersRouter />
			</Route>
			<Route path="/domain">
				<DomainsRouter />
			</Route>
			<Route path="/network">
				<NetworkRouter />
			</Route>
			<Route path="/services">
				<ServicesRouter />
			</Route>
			<Route path="/security">
				<SecurityRouter />
			</Route>
			<Route path="/shop">
				<ShopRouter />
			</Route>
			<Redirect path="/billing" to="/account/billing" />
			<RoleRestrictedRoute
				path="/account/billing/invoice/:invoiceId"
				component={InvoiceDetails}
				whitelistedRoles={[
					roles.ACCOUNT_OWNER,
					roles.SECONDARY_OWNER,
					roles.SUSPENDED_OWNER,
					roles.PURCHASER,
				]}
			/>
			<Route path="/account">
				<AccountRouter />
			</Route>
			<Route path="/cart-login">
				<Redirect to="/cart" />
			</Route>
			<Route path="/cart">
				<CartRouter />
			</Route>
			<Route path="/chat">
				<ChatRouter />
			</Route>
			<Route path="/oidc/callback">
				<OidcCallback />
			</Route>
			<RoleRestrictedRoute
				// legacy route
				exact
				whitelistedRoles={[
					roles.PURCHASER,
					roles.TECHNICIAN,
					roles.SUSPENDED_OWNER,
				]}
				path="/support/mytickets/:ticket"
				component={({ match }) => (
					<Redirect to={`/support/mycases/${match.params.ticket}`} />
				)}
			/>
			<RoleRestrictedRoute
				exact
				whitelistedRoles={[
					roles.PURCHASER,
					roles.TECHNICIAN,
					roles.SUSPENDED_OWNER,
				]}
				path="/support/mycases/:ticket"
				component={SupportCase}
			/>
			<Route path="/support">
				<SupportRouter />
			</Route>
			<Route path="/feedback">
				<FeedbackRouter />
			</Route>
			<RoleRestrictedRoute
				component={BackupsAndRecovery}
				path="/storage/backups-and-recovery"
				whitelistedRoles={[roles.PURCHASER, roles.TECHNICIAN]}
			/>
			<Route path="/access-denied">
				<AccessDenied />
			</Route>
			<RoleRestrictedRoute
				exact
				path="/migration-center"
				component={MigrationCenter}
				whitelistedRoles={[
					roles.PURCHASER,
					roles.TECHNICIAN,
					roles.SUSPENDED_OWNER,
				]}
			/>
			<Route>
				<NotFound />
			</Route>
		</Switch>
		<DialogNewPortalWelcome />
		<DialogUTMWrapper />
	</PagePreloadProvider>
);

const UnAuthenticatedRoutes = () => (
	<Switch>
		{/* the post purchase page and the checkout page are the shop pages that are protected */}
		<Route exact path="/shop/complete">
			<LoginRedirect />
		</Route>
		<Route exact path="/shop/checkout">
			<LoginRedirect />
		</Route>
		{AlwaysRoutesRaw()}
		<Route>
			<LoginRedirect />
		</Route>
	</Switch>
);

const KeroOnlyRoutes = () => (
	<Switch>
		{AlwaysRoutesRaw()}
		<Route path={ACCOUNT_CREATE_PATH}>
			<AccountCreate />
		</Route>
		<Route path="/logout">
			<Logout />
		</Route>
		<Redirect to={ACCOUNT_CREATE_PATH} />
	</Switch>
);

const NewAccountWithPurchaseRoutes = () => (
	<Switch>
		<Route path="/shop/complete">
			<OrderConfirmation />
		</Route>

		<Redirect to="/shop/complete" />
	</Switch>
);

const externalSite = () => (
	<Switch>
		<Route path="/external/chatLoading">
			<LiveChatLoading />
		</Route>
		<Route path="/external/chatUnavailable">
			<LiveChatUnavailable />
		</Route>
		<Route
			path="/external/invoicepdf/:id"
			render={(props) => <InvoicePDFDisplay {...props} />}
		/>
		<Route
			path="/external/vnc-console/:uniqId"
			render={(props) => <VNCConsole {...props} />}
		/>
		<Route>
			<NotFound />
		</Route>
	</Switch>
);

const Routes = ({
	hasAccountLogin,
	isLoggedIn,
	isKeroOnly,
	isBasketAdmin,
	verifificationRequired,
	isNewAccountWithPurchase,
	isAccountTerminated,
}) => {
	const { isLoading } = useAuth();

	if (isLoading) return <SuspenseFallback />;

	if (isLoggedIn && !hasAccountLogin && !isBasketAdmin && !isKeroOnly)
		return <RestrictionNoAccountLogin sx={{ flexGrow: 1 }} />;
	if (isAccountTerminated)
		return <RestrictionAccountTerminated sx={{ flexGrow: 1 }} />;
	if (verifificationRequired)
		return <RestrictionVerificationRequired sx={{ flexGrow: 1 }} />;
	if (isLoggedIn && isNewAccountWithPurchase)
		return <NewAccountWithPurchaseRoutes />;
	if (isLoggedIn && isKeroOnly) return <KeroOnlyRoutes />;
	if (isLoggedIn) return <AuthenticatedRoutes />;
	return <UnAuthenticatedRoutes />;
};

const PageStructure = () => {
	const { pathname } = useLocation();
	const isLoggedIn = useAuthenticated();
	const isKeroOnly = useKeroOnly();
	const isBasketAdmin = useBasketAdmin();
	const hasAccountLogin = useAccountLogin();
	const isSuspended = useSuspended();
	const { data: accountDetails } = useAccountDetails(
		{},
		{ enabled: isLoggedIn },
	);
	const { data: orderData } = useOrderList(
		{ page_size: 999, alsowith: ['veriffURL', 'veriffSessionStatus'] },
		{ enabled: isLoggedIn },
	);
	const veriffStatus = orderData?.items.find(
		(item) => item.order_id,
	)?.veriffSessionStatus;
	const isNewAccount =
		accountDetails?.account_status === accountStatuses.PENDING_NEW;
	const isAccountTerminated =
		accountDetails?.account_status === accountStatuses.TERMINATED;
	const isOrderRequireCustomerAction = orderData?.items.some(
		(order) => order.status === 'customer_action_required',
	);
	const isAccountVerificationComplete = orderData?.items.some(
		(order) =>
			order.veriffSessionStatus === 'submitted' ||
			order.veriffSessionStatus === 'approved' ||
			order.veriffSessionStatus === 'declined',
	);
	const isCurrentOrderRequireVerification =
		veriffStatus === 'created' ||
		veriffStatus === 'resubmission_requested' ||
		veriffStatus === 'abandoned' ||
		veriffStatus === 'expired';
	const verifificationRequired =
		isNewAccount &&
		isOrderRequireCustomerAction &&
		!isAccountVerificationComplete &&
		isCurrentOrderRequireVerification;
	const isNewAccountWithPurchase = isNewAccount && orderData?.items.length > 0;

	return (
		<>
			<LeftHandLayout
				appPath={pathname}
				linkComponent={RouterLink}
				linkProp="to"
				roles={{
					loggedIn: isLoggedIn,
					keroOnly: isKeroOnly,
					basketAdmin: isBasketAdmin,
					accountLogin: hasAccountLogin,
					suspended: isSuspended,
					terminated: isAccountTerminated,
					verificationRequired: verifificationRequired,
				}}
				minHeight="100vh"
				minWidth="100vw"
				maxWidth="100vw"
			>
				<Suspense fallback={<SuspenseFallback />}>
					<Routes
						hasAccountLogin={hasAccountLogin}
						isLoggedIn={isLoggedIn}
						isKeroOnly={isKeroOnly}
						isBasketAdmin={isBasketAdmin}
						verifificationRequired={verifificationRequired}
						isNewAccountWithPurchase={isNewAccountWithPurchase}
						isAccountTerminated={isAccountTerminated}
					/>
				</Suspense>
			</LeftHandLayout>
			<Errors />
			<GlobalComponents />
		</>
	);
};

const RouteHandler = () => {
	return (
		<Suspense fallback={<SuspenseFallback />}>
			<Switch>
				<Route path="/external" component={externalSite} />
				<Route>
					<PageStructure />
				</Route>
			</Switch>
		</Suspense>
	);
};

export default RouteHandler;
